import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Car, ArrowRight, ShieldCheck } from 'lucide-react';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_yachangu-pay/artifacts/ynseryay_EE6C3FEA-5C5E-424A-B08C-ED1F2A53B790.jpeg';

const Register = () => {
  const navigate = useNavigate();

  const handleRoleSelect = (role) => {
    navigate(role === 'rider' ? '/rider/auth' : '/driver/auth');
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-5 py-10 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(120% 90% at 50% -10%, #1f7a3d 0%, #0d5327 34%, #063217 62%, #02160c 100%)',
      }}
      data-testid="register-page"
    >
      <div className="pointer-events-none absolute -top-24 right-[-60px] w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-80px] left-[-40px] w-80 h-80 rounded-full bg-emerald-600/20 blur-3xl" />

      <div className="w-full max-w-md relative">
        {/* Floating logo */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-[22px] overflow-hidden shadow-2xl ring-1 ring-white/20 bg-white/10 backdrop-blur-md">
            <img src={LOGO_URL} alt="Yachangu" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Glass card */}
        <div
          className="rounded-[32px] border border-white/10 px-7 py-9 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
          style={{ background: 'rgba(9, 46, 26, 0.45)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)' }}
          data-testid="register-card"
        >
          <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-white tracking-tight">Create Account</h1>
            <p className="text-emerald-100/70 mt-2 text-base">Choose how you want to use Yachangu.</p>
          </div>

          <div className="space-y-4">
            {/* Rider */}
            <button
              onClick={() => handleRoleSelect('rider')}
              data-testid="register-role-rider"
              className="group w-full flex items-center justify-between gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-emerald-400/50 active:scale-[0.99] transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                  <User className="w-7 h-7 text-emerald-300 group-hover:text-emerald-950" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-lg text-white">Register as Rider</h3>
                  <p className="text-sm text-emerald-100/60">Book rides and travel safely</p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-emerald-200/50 group-hover:text-emerald-300 group-hover:translate-x-1 transition-all" />
            </button>

            {/* Driver */}
            <button
              onClick={() => handleRoleSelect('driver')}
              data-testid="register-role-driver"
              className="group w-full flex items-center justify-between gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-emerald-400/50 active:scale-[0.99] transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                  <Car className="w-7 h-7 text-emerald-300 group-hover:text-emerald-950" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-lg text-white">Register as Driver</h3>
                  <p className="text-sm text-emerald-100/60">Earn money by giving rides</p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-emerald-200/50 group-hover:text-emerald-300 group-hover:translate-x-1 transition-all" />
            </button>
          </div>

          <p className="text-center text-emerald-50/80 mt-8 text-sm">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              data-testid="register-login-link"
              className="font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Login
            </button>
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mt-7 text-emerald-100/35">
          <ShieldCheck className="w-4 h-4" />
          <span className="text-xs font-semibold tracking-[0.15em]">SECURED BY YACHANGU</span>
        </div>
      </div>
    </div>
  );
};

export default Register;
